// frontend/src/modules/marginCenter/services/marginCenterService.ts

import axiosInstance from '../../../config/axiosInstance';

import type {
  MarginCenterCadenceRadarParams,
  MarginCenterCadenceRadarResponse,
  MarginCenterCapabilitiesResponse,
  MarginCenterCommercialAskBody,
  MarginCenterCommercialAskResponse,
  MarginCenterCommercialQueryBody,
  MarginCenterCommercialQueryResponse,
  MarginCenterCustomerContextParams,
  MarginCenterCustomerContextResponse,
  MarginCenterCustomerRelationshipsParams,
  MarginCenterCustomerRelationshipsResponse,
  MarginCenterCustomerProductActivityParams,
  MarginCenterCustomerProductActivityResponse,
  MarginCenterCustomerProductBenchmarkParams,
  MarginCenterCustomerProductBenchmarkResponse,
  MarginCenterCustomerProductContextParams,
  MarginCenterCustomerProductContextResponse,
  MarginCenterCustomerProductHistoryParams,
  MarginCenterCustomerProductHistoryResponse,
  MarginCenterExplorerParams,
  MarginCenterExplorerResponse,
  MarginCenterFollowUpCloseBody,
  MarginCenterFollowUpCreateBody,
  MarginCenterFollowUpNoteBody,
  MarginCenterFollowUpPortfolioParams,
  MarginCenterFollowUpPortfolioResponse,
  MarginCenterFollowUpResponse,
  MarginCenterFollowUpStatusBody,
  MarginCenterFollowUpPlanningBody,
  MarginCenterOverviewParams,
  MarginCenterOverviewResponse,
  MarginCenterProductContextParams,
  MarginCenterProductContextResponse,
  MarginCenterProductCustomersParams,
  MarginCenterProductCustomersResponse,
} from '../types/marginCenter';

import type {
  MarginCenterDynamicComparablesRequest,
  MarginCenterDynamicComparablesResponse,
} from '../types/dynamicComparables';

import type {
  MarginCenterCustomerEconomicStudyParams,
  MarginCenterCustomerEconomicStudyResponse,
} from '../types/customerEconomicStudy';

import type {
  MarginCenterProductEconomicStudyParams,
  MarginCenterProductEconomicStudyResponse,
} from '../types/productEconomicStudy';

import type {
  MarginCenterCustomerKpiBreakdownParams,
  MarginCenterCustomerKpiBreakdownResponse,
} from '../types/customerKpiBreakdown';

import type {
  MarginCenterProductKpiBreakdownParams,
  MarginCenterProductKpiBreakdownResponse,
} from '../types/productKpiBreakdown';

import type {
  MarginCenterCustomerPositionParams,
  MarginCenterCustomerPositionResponse,
} from '../types/customerPosition';

import type {
  MarginCenterCustomerPositionRankingParams,
  MarginCenterCustomerPositionRankingResponse,
} from '../types/customerPositionRanking';

import type {
  MarginCenterProductPositionParams,
  MarginCenterProductPositionResponse,
} from '../types/productPosition';

import type {
  MarginCenterProductPositionRankingParams,
  MarginCenterProductPositionRankingResponse,
} from '../types/productPositionRanking';

import type {
  MarginCenterCustomerPortfolioDetailParams,
  MarginCenterCustomerPortfolioDetailResponse,
} from '../types/customerPortfolioDetail';

import type {
  MarginCenterProductCustomerPortfolioDetailParams,
  MarginCenterProductCustomerPortfolioDetailResponse,
} from '../types/productCustomerPortfolioDetail';

import type {
  MarginCenterFollowUpInventoryParams,
  MarginCenterFollowUpInventoryResponse,
  MarginCenterFollowUpInventoryV2Params,
  MarginCenterFollowUpInventoryV2Response,
} from '../types/followUpInventory';

import type {
  MarginCenterFollowUpAttentionParams,
  MarginCenterFollowUpAttentionResponse,
} from '../types/followUpAttention';

import type {
  MarginCenterFollowUpPlanningParams,
  MarginCenterFollowUpPlanningResponse,
} from '../types/followUpPlanning';

import type {
  MarginCenterCadenceAttentionFollowUpBody,
  MarginCenterCadenceAttentionFollowUpResponse,
  MarginCenterCadenceAttentionParams,
  MarginCenterCadenceAttentionResolveBody,
  MarginCenterCadenceAttentionResolveResponse,
  MarginCenterCadenceAttentionResponse,
  MarginCenterCadenceAttentionResolveV2Body,
  MarginCenterCadenceAttentionResolveV2Response,
  MarginCenterCadenceAttentionFollowUpV2Body,
  CadenceFollowUpEpisodesResponse,
  RelationshipActivityRequestItem,
  RelationshipActivityResponse,
  SubstituteProductsResponse,
} from '../types/cadenceAttention';

const MARGIN_CENTER_BASE_PATH = '/pricing/margin-center';

export async function getMarginCenterDynamicComparables(
  request: MarginCenterDynamicComparablesRequest,
): Promise<MarginCenterDynamicComparablesResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterDynamicComparablesResponse>(
      `${MARGIN_CENTER_BASE_PATH}/comparables`,
      request,
    );

  return data;
}

export async function getMarginCenterCapabilities():
  Promise<MarginCenterCapabilitiesResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCapabilitiesResponse>(
      `${MARGIN_CENTER_BASE_PATH}/capabilities`,
    );

  return data;
}

export async function getMarginCenterOverview(
  params: MarginCenterOverviewParams = {},
): Promise<MarginCenterOverviewResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterOverviewResponse>(
      `${MARGIN_CENTER_BASE_PATH}/overview`,
      {
        params,
      },
    );

  return data;
}

export async function executeMarginCenterCommercialQuery(
  body: MarginCenterCommercialQueryBody,
): Promise<MarginCenterCommercialQueryResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterCommercialQueryResponse>(
      `${MARGIN_CENTER_BASE_PATH}/commercial-query`,
      body,
    );

  return data;
}

export async function askMarginCenterCommercialQuery(
  body: MarginCenterCommercialAskBody,
): Promise<MarginCenterCommercialAskResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterCommercialAskResponse>(
      `${MARGIN_CENTER_BASE_PATH}/commercial-query/ask`,
      body,
    );

  return data;
}

export async function getMarginCenterCadenceRadar(
  params: MarginCenterCadenceRadarParams = {},
): Promise<MarginCenterCadenceRadarResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCadenceRadarResponse>(
      `${MARGIN_CENTER_BASE_PATH}/radar/cadence`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCadenceAttention(
  params: MarginCenterCadenceAttentionParams = {},
): Promise<MarginCenterCadenceAttentionResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCadenceAttentionResponse>(
      `${MARGIN_CENTER_BASE_PATH}/attention/cadence`,
      {
        params: {
          lifecycle: params.lifecycle,
          limit: params.limit,
          cursor: params.cursor,
        },
      },
    );

  return data;
}

export async function openOrLinkMarginCenterCadenceFollowUp(
  customerId: string,
  productId: string,
  body: MarginCenterCadenceAttentionFollowUpBody,
): Promise<MarginCenterCadenceAttentionFollowUpResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterCadenceAttentionFollowUpResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/attention/cadence/follow-up`,
      body,
    );

  return data;
}

export async function resolveMarginCenterCadenceOccurrence(
  customerId: string,
  productId: string,
  body: MarginCenterCadenceAttentionResolveBody,
): Promise<MarginCenterCadenceAttentionResolveResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterCadenceAttentionResolveResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/attention/cadence/resolve`,
      body,
    );

  return data;
}

export async function getMarginCenterFollowUpPortfolio(
  params: MarginCenterFollowUpPortfolioParams = {},
): Promise<MarginCenterFollowUpPortfolioResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterFollowUpPortfolioResponse>(
      `${MARGIN_CENTER_BASE_PATH}/follow-ups`,
      {
        params,
      },
    );

  return data;
}


// DUX-03D: única consulta del inventario para global/cliente/producto/pareja.
export async function getMarginCenterFollowUpInventory(
  params: MarginCenterFollowUpInventoryParams,
): Promise<MarginCenterFollowUpInventoryResponse> {
  const { data } = await axiosInstance.get<MarginCenterFollowUpInventoryResponse>(
    `${MARGIN_CENTER_BASE_PATH}/follow-ups/inventory`,
    { params: {
      context: params.context,
      customer_id: params.customer_id,
      product_id: params.product_id,
      status: params.status,
      scope: params.scope,
      match: params.match,
      search: params.search,
      sort: params.sort,
      created_from: params.created_from,
      created_before: params.created_before,
      limit: params.limit,
      cursor: params.cursor,
    } },
  );
  return data;
}

export async function getMarginCenterFollowUpAttention(
  params: MarginCenterFollowUpAttentionParams = {},
): Promise<MarginCenterFollowUpAttentionResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterFollowUpAttentionResponse>(
      `${MARGIN_CENTER_BASE_PATH}/follow-ups/attention`,
      {
        params: {
          state: params.state,
          horizon_days: params.horizon_days,
          limit: params.limit,
          cursor: params.cursor,
        },
      },
    );

  return data;
}

export async function getMarginCenterFollowUpPlanning(
  params: MarginCenterFollowUpPlanningParams = {},
): Promise<MarginCenterFollowUpPlanningResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterFollowUpPlanningResponse>(
      `${MARGIN_CENTER_BASE_PATH}/follow-ups/planning`,
      {
        params: {
          state: params.state,
          limit: params.limit,
          cursor: params.cursor,
        },
      },
    );

  return data;
}

export async function createMarginCenterFollowUp(
  customerId: string,
  productId: string,
  body: MarginCenterFollowUpCreateBody,
): Promise<MarginCenterFollowUpResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterFollowUpResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/follow-ups`,
      body,
    );

  return data;
}

export async function getMarginCenterFollowUp(
  followUpId: string,
): Promise<MarginCenterFollowUpResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterFollowUpResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/follow-ups/${
        encodeURIComponent(followUpId)
      }`,
    );

  return data;
}

export async function addMarginCenterFollowUpNote(
  followUpId: string,
  body: MarginCenterFollowUpNoteBody,
): Promise<MarginCenterFollowUpResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterFollowUpResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/follow-ups/${
        encodeURIComponent(followUpId)
      }/notes`,
      body,
    );

  return data;
}

export async function setMarginCenterFollowUpStatus(
  followUpId: string,
  body: MarginCenterFollowUpStatusBody,
): Promise<MarginCenterFollowUpResponse> {
  const { data } =
    await axiosInstance.patch<MarginCenterFollowUpResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/follow-ups/${
        encodeURIComponent(followUpId)
      }/status`,
      body,
    );

  return data;
}

export async function updateMarginCenterFollowUpPlanning(
  followUpId: string,
  body: MarginCenterFollowUpPlanningBody,
): Promise<MarginCenterFollowUpResponse> {
  const { data } =
    await axiosInstance.patch<MarginCenterFollowUpResponse>(
      `${MARGIN_CENTER_BASE_PATH}/follow-ups/${encodeURIComponent(followUpId)}/planning`,
      body,
    );
  return data;
}

export async function closeMarginCenterFollowUp(
  followUpId: string,
  body: MarginCenterFollowUpCloseBody,
): Promise<MarginCenterFollowUpResponse> {
  const { data } =
    await axiosInstance.post<MarginCenterFollowUpResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/follow-ups/${
        encodeURIComponent(followUpId)
      }/close`,
      body,
    );

  return data;
}

export async function getMarginCenterExplorer(
  params: MarginCenterExplorerParams = {},
): Promise<MarginCenterExplorerResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterExplorerResponse>(
      `${MARGIN_CENTER_BASE_PATH}/explorer`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterProductContext(
  productId: string,
  params: MarginCenterProductContextParams = {},
): Promise<MarginCenterProductContextResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductContextResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterProductPosition(
  productId: string,
  params: MarginCenterProductPositionParams = {},
): Promise<MarginCenterProductPositionResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductPositionResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }/position`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterProductPositionRanking(
  productId: string,
  params: MarginCenterProductPositionRankingParams,
): Promise<MarginCenterProductPositionRankingResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductPositionRankingResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }/position-ranking`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterProductEconomicStudy(
  productId: string,
  params: MarginCenterProductEconomicStudyParams = {},
): Promise<MarginCenterProductEconomicStudyResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductEconomicStudyResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }/economic-study`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterProductKpiBreakdown(
  productId: string,
  params: MarginCenterProductKpiBreakdownParams,
): Promise<MarginCenterProductKpiBreakdownResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductKpiBreakdownResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }/kpi-breakdown`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterProductCustomers(
  productId: string,
  params: MarginCenterProductCustomersParams = {},
): Promise<MarginCenterProductCustomersResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductCustomersResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }/customers`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterProductCustomerPortfolioDetail(
  productId: string,
  params: MarginCenterProductCustomerPortfolioDetailParams,
): Promise<MarginCenterProductCustomerPortfolioDetailResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterProductCustomerPortfolioDetailResponse>(
      `${MARGIN_CENTER_BASE_PATH}/products/${
        encodeURIComponent(productId)
      }/portfolio-detail`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerContext(
  customerId: string,
  params: MarginCenterCustomerContextParams = {},
): Promise<MarginCenterCustomerContextResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerContextResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(customerId)}`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerPosition(
  customerId: string,
  params: MarginCenterCustomerPositionParams = {},
): Promise<MarginCenterCustomerPositionResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerPositionResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(
        customerId,
      )}/position`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterCustomerPositionRanking(
  customerId: string,
  params: MarginCenterCustomerPositionRankingParams,
): Promise<MarginCenterCustomerPositionRankingResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerPositionRankingResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(
        customerId,
      )}/position-ranking`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterCustomerPortfolioDetail(
  customerId: string,
  params: MarginCenterCustomerPortfolioDetailParams,
): Promise<MarginCenterCustomerPortfolioDetailResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerPortfolioDetailResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(
        customerId,
      )}/portfolio-detail`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterCustomerEconomicStudy(
  customerId: string,
  params: MarginCenterCustomerEconomicStudyParams = {},
): Promise<MarginCenterCustomerEconomicStudyResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerEconomicStudyResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(
        customerId,
      )}/economic-study`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerKpiBreakdown(
  customerId: string,
  params: MarginCenterCustomerKpiBreakdownParams,
): Promise<MarginCenterCustomerKpiBreakdownResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerKpiBreakdownResponse>(
      `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(
        customerId,
      )}/kpi-breakdown`,
      {
        params,
      },
    );

  return data;
}


export async function getMarginCenterCustomerRelationships(
  customerId: string,
  params: MarginCenterCustomerRelationshipsParams = {},
): Promise<MarginCenterCustomerRelationshipsResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerRelationshipsResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/customers/${
        encodeURIComponent(customerId)
      }/relationships`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerProductContext(
  customerId: string,
  productId: string,
  params: MarginCenterCustomerProductContextParams = {},
): Promise<MarginCenterCustomerProductContextResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerProductContextResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerProductActivity(
  customerId: string,
  productId: string,
  params: MarginCenterCustomerProductActivityParams = {},
): Promise<MarginCenterCustomerProductActivityResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerProductActivityResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/activity`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerProductBenchmark(
  customerId: string,
  productId: string,
  params: MarginCenterCustomerProductBenchmarkParams = {},
): Promise<MarginCenterCustomerProductBenchmarkResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerProductBenchmarkResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/benchmark`,
      {
        params,
      },
    );

  return data;
}

export async function getMarginCenterCustomerProductHistory(
  customerId: string,
  productId: string,
  params: MarginCenterCustomerProductHistoryParams = {},
): Promise<MarginCenterCustomerProductHistoryResponse> {
  const { data } =
    await axiosInstance.get<MarginCenterCustomerProductHistoryResponse>(
      `${
        MARGIN_CENTER_BASE_PATH
      }/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/history`,
      {
        params,
      },
    );

  return data;
}

// --- PRC-048: gestión de avisos de cadencia ---

export async function resolveMarginCenterCadenceOccurrenceV2(
  customerId: string,
  productId: string,
  body: MarginCenterCadenceAttentionResolveV2Body,
): Promise<MarginCenterCadenceAttentionResolveV2Response> {
  const { data } = await axiosInstance.post<MarginCenterCadenceAttentionResolveV2Response>(
    `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(customerId)}/relationships/${encodeURIComponent(productId)}/attention/cadence/resolve-v2`,
    body,
  );
  return data;
}

export async function openOrLinkMarginCenterCadenceFollowUpV2(
  customerId: string,
  productId: string,
  body: MarginCenterCadenceAttentionFollowUpV2Body,
): Promise<MarginCenterCadenceAttentionFollowUpResponse> {
  const { data } = await axiosInstance.post<MarginCenterCadenceAttentionFollowUpResponse>(
    `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(customerId)}/relationships/${encodeURIComponent(productId)}/attention/cadence/follow-up-v2`,
    body,
  );
  return data;
}

export async function getMarginCenterCadenceFollowUpEpisodes(
  params: { limit?: number; offset?: number } = {},
): Promise<CadenceFollowUpEpisodesResponse> {
  const { data } = await axiosInstance.get<CadenceFollowUpEpisodesResponse>(
    `${MARGIN_CENTER_BASE_PATH}/attention/cadence/follow-ups`,
    { params: { limit: params.limit, offset: params.offset } },
  );
  return data;
}

export async function getMarginCenterRelationshipActivity12m(
  items: RelationshipActivityRequestItem[],
): Promise<RelationshipActivityResponse> {
  const { data } = await axiosInstance.post<RelationshipActivityResponse>(
    `${MARGIN_CENTER_BASE_PATH}/relationships/activity-12m`,
    { items },
  );
  return data;
}

export async function getMarginCenterSubstituteProducts(
  customerId: string,
  params: { since?: string | null; excludeProductId?: string | null; q?: string | null; limit?: number },
): Promise<SubstituteProductsResponse> {
  const { data } = await axiosInstance.get<SubstituteProductsResponse>(
    `${MARGIN_CENTER_BASE_PATH}/customers/${encodeURIComponent(customerId)}/substitute-products`,
    {
      params: {
        since: params.since || undefined,
        exclude_product_id: params.excludeProductId || undefined,
        q: params.q || undefined,
        limit: params.limit,
      },
    },
  );
  return data;
}

// --- PRC-049: Seguimientos con origen, vista y recuentos ---
export async function getMarginCenterFollowUpInventoryV2(
  params: MarginCenterFollowUpInventoryV2Params,
): Promise<MarginCenterFollowUpInventoryV2Response> {
  const { data } = await axiosInstance.get<MarginCenterFollowUpInventoryV2Response>(
    `${MARGIN_CENTER_BASE_PATH}/follow-ups/inventory-v2`,
    { params: {
      context: params.context,
      customer_id: params.customer_id,
      product_id: params.product_id,
      status: params.status,
      scope: params.scope,
      match: params.match,
      search: params.search,
      sort: params.sort,
      created_from: params.created_from,
      created_before: params.created_before,
      limit: params.limit,
      cursor: params.cursor,
      origin: params.origin,
      view: params.view,
    } },
  );
  return data;
}
